import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { authProvider } from '../utils/authProvider';
import { Environment } from '../utils/Environment';
import { useToast } from '../layout/Layout';
import './A3Repository.css';
import {
  EMPTY_STATE_MESSAGES,
  SUCCESS_MESSAGES,
  ERROR_MESSAGES
} from './constants/A3Constants';
import { exportA3ToPdf } from './utils/A3PdfExport';

interface A3Form {
  a3Id: string;
  title: string;
  status: string;
  date: string;
  ownerId: string;
  tag: string;
  savings: number;
  a3SiteName: string;
  a3Region: string;
}

const A3Repository: React.FC = () => {
  const auth = authProvider();
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError } = useToast();

  const [loading, setLoading] = useState(false);
  const [a3Forms, setA3Forms] = useState<A3Form[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [filterSearch, setFilterSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSite, setFilterSite] = useState('');
  const [filterOwner, setFilterOwner] = useState('');
  const [filterRegion, setFilterRegion] = useState('');
  const [sortBy, setSortBy] = useState('lastUpdated');
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const fetchA3Forms = useCallback(async () => {
    const userId = auth.userId;

    if (!userId) {
      setError('User ID not available.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const apiUrl =
        `${Environment.fetchA3FormsAPI()}?userId=${userId}`;

      const response = await axios.get<A3Form[]>(apiUrl, {
        timeout: 10000,
      });

      setA3Forms(response.data || []);
    } catch (err) {
      console.error(err);
      setError(ERROR_MESSAGES.LOAD_FORMS_FAILED);
    } finally {
      setLoading(false);
    }
  }, [auth.userId]);

  useEffect(() => {
    fetchA3Forms();
  }, [fetchA3Forms]);

  // Scroll to top whenever the location changes (page is navigated to)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

const totalSavings = a3Forms.reduce(
  (sum, item) => sum + (item.savings || 0),
  0
);

const implementedCount = a3Forms.filter(
  item => item.status === 'Completed'
).length;

const inProgressCount = a3Forms.filter(
  item => item.status === 'In Progress'
).length;

const uniqueSites = Array.from(new Set(a3Forms.map(f => f.a3SiteName).filter(Boolean)));
const uniqueOwners = Array.from(new Set(a3Forms.map(f => f.ownerId).filter(Boolean)));
const uniqueRegions = Array.from(new Set(a3Forms.map(f => f.a3Region).filter(Boolean)));

// Calculate insights dynamically (case-insensitive)
const tagCounts: Record<string, number> = {};
a3Forms.forEach(form => {
  if (form.tag) {
    const tags = form.tag.split(',').map(t => t.trim()).filter(Boolean);
    tags.forEach(tag => {
      const tagLower = tag.toLowerCase();
      tagCounts[tagLower] = (tagCounts[tagLower] || 0) + 1;
    });
  }
});

// Get all tags with the highest count and format with proper capitalization
const sortedTags = Object.entries(tagCounts).sort((a, b) => b[1] - a[1]);
const mostCommonTags = sortedTags.length > 0 
  ? sortedTags.filter(([_, count]) => count === sortedTags[0][1]).map(([tag, count]) => {
      // Capitalize first letter, rest lowercase
      const formatted = tag.charAt(0).toUpperCase() + tag.slice(1).toLowerCase();
      return [formatted, count] as [string, number];
    })
  : [];

const regionCounts: Record<string, number> = {};
a3Forms.forEach(form => {
  if (form.a3Region) {
    regionCounts[form.a3Region] = (regionCounts[form.a3Region] || 0) + 1;
  }
});

const completedForms = a3Forms.filter(f => f.status === 'Completed');
const completedWithSavings = completedForms.filter(f => f.savings && f.savings > 0);
const avgSavings = completedWithSavings.length > 0
  ? completedWithSavings.reduce((sum, f) => sum + (f.savings || 0), 0) / completedWithSavings.length
  : 0;

const filteredForms = a3Forms.filter(f => {
  const searchLower = filterSearch.toLowerCase();
  const matchesSearch = !filterSearch || 
    f.title?.toLowerCase().includes(searchLower) ||
    f.tag?.toLowerCase().includes(searchLower);
  const matchesStatus = !filterStatus || f.status === filterStatus;
  const matchesSite = !filterSite || f.a3SiteName === filterSite;
  const matchesOwner = !filterOwner || f.ownerId === filterOwner;
  const matchesRegion = !filterRegion || f.a3Region === filterRegion;
  return matchesSearch && matchesStatus && matchesSite && matchesOwner && matchesRegion;
});

// Sort filtered forms
const sortedForms = [...filteredForms].sort((a, b) => {
  switch (sortBy) {
    case 'lastUpdated':
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    case 'titleAsc':
      return a.title.localeCompare(b.title);
    case 'titleDesc':
      return b.title.localeCompare(a.title);
    case 'savingsDesc':
      return (b.savings || 0) - (a.savings || 0);
    case 'savingsAsc':
      return (a.savings || 0) - (b.savings || 0);
    case 'status':
      return a.status.localeCompare(b.status);
    default:
      return 0;
  }
});

const hasActiveFilters = filterSearch || filterStatus || filterSite || filterOwner || filterRegion;

const resetFilters = () => {
  setFilterSearch('');
  setFilterStatus('');
  setFilterSite('');
  setFilterOwner('');
  setFilterRegion('');
};

const getStatusClass = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'completed':
      return 'a3-card-status-implemented';

    case 'in progress':
      return 'a3-card-status-inprogress';

    case 'not started':
      return 'a3-card-status-notstarted';

    default:
      return 'a3-card-status-default';
  }
};

const formatRegionComparison = (entry: [string, number], idx: number) => {
  if (idx === 0) {
    const plural = entry[1] > 1 ? 's' : '';
    return `${entry[0]} has ${entry[1]} A3${plural}`;
  }
  return ` vs. ${entry[1]} in ${entry[0]}`;
};

  if (loading) {
    return (
      <div className="a3-repository-container">
        <h2><strong>A3 Repository</strong></h2>
        <p className="a3-repository-subtitle">Search and reuse problem-solving work across the network</p>
        <div className="a3-repo-loading-container">
          <div className="a3-repo-spinner"></div>
          <div className="a3-repo-loading-text">
            Loading A3 Forms<span className="a3-repo-loading-dots"></span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="a3-repository-container">
        <h2><strong>A3 Repository</strong></h2>
        <p className="a3-repository-subtitle">Search and reuse problem-solving work across the network</p>
        <p className="a3-repo-error">{error}</p>
      </div>
    );
  }

  
  return (
    <div className="a3-repository-container">
      {/* Sticky Header and KPI Section */}
      <div style={{
        position: 'sticky',
        top: 0,
        backgroundColor: '#fff',
        zIndex: 1,
        paddingBottom: '12px',
        marginBottom: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}>
        <div className="a3-repository-header">
          <div>
            <h2><strong>A3 Repository</strong></h2>
            <p className="a3-repository-subtitle">Search and reuse problem-solving work across the network</p>
          </div>
          <button 
            className="btn action-button new-a3-btn" 
            onClick={() => navigate('/wms-upload-ui/ie-hub/a3-builder')}
            style={{ marginTop: '8px' }}
          >
            + New A3
          </button>
        </div>

        <div className="a3-kpi-row">
          <div className="a3-kpi-card">
            <div className="a3-kpi-value">
              {a3Forms.length}
            </div>
            <div className="a3-kpi-label">
              Total A3s
            </div>
          </div>

          <div className="a3-kpi-card">
            <div className="a3-kpi-value">
              {implementedCount}
            </div>
            <div className="a3-kpi-label">
              Completed
            </div>
          </div>

          <div className="a3-kpi-card">
            <div className="a3-kpi-value">
              {inProgressCount}
            </div>
            <div className="a3-kpi-label">
              In Progress
            </div>
          </div>

          <div className="a3-kpi-card">
            <div className="a3-kpi-value">
              ${totalSavings.toLocaleString()}
            </div>
            <div className="a3-kpi-label">
              Validated Savings
            </div>
          </div>
        </div>
      </div>

      {a3Forms.length === 0 && (
        <p>{EMPTY_STATE_MESSAGES.NO_A3_FORMS_FOUND}</p>
      )}

      <div className="a3-content-layout">
        {/* ── Left Column: Filters + Insights ── */}
        <div className="a3-left-column" style={{
          position: 'sticky',
          top: '220px',
          alignSelf: 'flex-start'
        }}>
          {/* Filters Panel */}
          <aside className="a3-filters-panel">
          <div className="a3-filters-title">Filters</div>

          <div className="a3-filter-group">
            <label className="a3-filter-label">Search</label>
            <input
              type="text"
              className="a3-filter-input"
              placeholder="Title or tag..."
              value={filterSearch}
              onChange={e => setFilterSearch(e.target.value)}
            />
          </div>

          <div className="a3-filter-group">
            <label className="a3-filter-label">Status</label>
            <select
              className="a3-filter-select"
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
            >
              <option value="">All</option>
              <option value="Not Started">Not Started</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
            </select>
          </div>

          <div className="a3-filter-group">
            <label className="a3-filter-label">Site</label>
            <select
              className="a3-filter-select"
              value={filterSite}
              onChange={e => setFilterSite(e.target.value)}
            >
              <option value="">All</option>
              {uniqueSites.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div className="a3-filter-group">
            <label className="a3-filter-label">Owner</label>
            <select
              className="a3-filter-select"
              value={filterOwner}
              onChange={e => setFilterOwner(e.target.value)}
            >
              <option value="">All</option>
              {uniqueOwners.map(o => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>

          <div className="a3-filter-group">
            <label className="a3-filter-label">Region</label>
            <select
              className="a3-filter-select"
              value={filterRegion}
              onChange={e => setFilterRegion(e.target.value)}
            >
              <option value="">All</option>
              {uniqueRegions.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>

          <button
            className="a3-filter-reset-btn"
            onClick={resetFilters}
            disabled={!hasActiveFilters}
          >
            Reset Filters
          </button>
        </aside>

          {/* Insights Box */}
          <div className="a3-insights-box">
            <div className="a3-insights-title">Insights</div>
            <ul className="a3-insights-list">
              {mostCommonTags.length > 0 && (
                <li>
                  <strong>{mostCommonTags.map(([tag]) => tag).join(', ')}</strong> {mostCommonTags.length > 1 ? 'are' : 'is'} the most common tag{mostCommonTags.length > 1 ? 's' : ''} — {mostCommonTags[0][1]} of {a3Forms.length} A3s.
                </li>
              )}
              {Object.keys(regionCounts).length > 1 && (
                <li>
                  {Object.entries(regionCounts)
                    .sort((a, b) => b[1] - a[1])
                    .map((entry, idx) => formatRegionComparison(entry, idx))
                    .join('')}.
                </li>
              )}
              {completedWithSavings.length > 0 && (
                <li>
                  ${avgSavings.toLocaleString(undefined, { maximumFractionDigits: 0 })} average validated savings across {completedWithSavings.length} completed A3{completedWithSavings.length > 1 ? 's' : ''}.
                </li>
              )}
              {a3Forms.length === 0 && (
                <li>No A3 forms available yet. Create your first A3 to see insights!</li>
              )}
              {a3Forms.length > 0 && Object.keys(tagCounts).length === 0 && completedWithSavings.length === 0 && (
                <li>Add tags and track savings to see more insights!</li>
              )}
            </ul>
          </div>
        </div>

        {/* ── Cards Grid ── */}
        <div className="a3-repository-grid">
          {/* Results count and sort info */}
          {filteredForms.length > 0 && (
            <div style={{
              gridColumn: '1 / -1',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
              paddingBottom: '12px',
              borderBottom: '2px solid #e2e8f0'
            }}>
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, #0369a1 0%, #0284c7 100%)',
                  color: '#fff',
                  padding: '8px 16px',
                  borderRadius: '20px',
                  fontWeight: '700',
                  fontSize: '14px',
                  boxShadow: '0 2px 8px rgba(3, 105, 161, 0.2)'
                }}>
                  {filteredForms.length} Result{filteredForms.length !== 1 ? 's' : ''}
                </div>
                <span style={{ 
                  color: '#64748b',
                  fontSize: '13px',
                  fontWeight: '500'
                }}>
                  of {a3Forms.length} total
                </span>
              </div>
              
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px'
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 6h18M7 12h10M10 18h4"></path>
                </svg>
                <select
                  id="sortBy"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  style={{ 
                    padding: '8px 36px 8px 12px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#1e293b',
                    background: '#fff',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    outline: 'none',
                    appearance: 'none',
                    backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'16\' height=\'16\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%231e293b\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3E%3Cpolyline points=\'6 9 12 15 18 9\'%3E%3C/polyline%3E%3C/svg%3E")',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 10px center',
                    backgroundSize: '16px',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#0369a1';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(3, 105, 161, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                  }}
                >
                  <option value="lastUpdated">� Recently Updated</option>
                  <option value="titleAsc">A → Z</option>
                  <option value="titleDesc">Z → A</option>
                  <option value="savingsDesc">� Highest Savings</option>
                  <option value="savingsAsc">� Lowest Savings</option>
                  <option value="status">⚡ By Status</option>
                </select>
              </div>
            </div>
          )}

          {filteredForms.length === 0 && (
            <div className="a3-empty-state">
              <div className="a3-empty-state-icon">🔍</div>
              <div className="a3-empty-state-title">{EMPTY_STATE_MESSAGES.NO_A3_FORMS_FOUND}</div>
              <div className="a3-empty-state-suggestion">
                Try adjusting your filters or search criteria
              </div>
            </div>
          )}
          {sortedForms.map((a3) => (
          <div key={a3.a3Id} className="a3-summary-card">
            <div>
              <div className="a3-card-header">
                <h3 className="a3-card-title">
                  {a3.title}
                </h3>

                <span
                  className={`a3-card-status-badge ${getStatusClass(a3.status)}`}
                >
                  {a3.status}
                </span>
              </div>
              {a3.tag && (
                <div className="a3-card-detail-item" style={{ marginTop: '8px', marginBottom: '8px' }}>
                  <span className="a3-card-tag">🏷️ {a3.tag}</span>
                </div>
              )}
              <div className="a3-card-detail-item">
                <span style={{ fontWeight: 'bold' }}>{a3.ownerId}</span>
                {a3.a3SiteName && <span> • {a3.a3SiteName}</span>}
                {a3.a3Region && <span> • {a3.a3Region}</span>}
              </div>
              <div className="a3-card-detail-item">Updated {a3.date}</div>
              <div className="a3-card-detail-item">
                {a3.savings ? (
                  <span className="a3-card-savings-value">
                    ${a3.savings.toLocaleString()} validated savings
                  </span>
                ) : (
                  <span className="a3-card-savings-not-recorded">
                    {EMPTY_STATE_MESSAGES.SAVINGS_NOT_RECORDED}
                  </span>
                )}
              </div>
            </div>

            <div className="a3-card-actions">
              <button
                onClick={() => navigate(`/wms-upload-ui/ie-hub/a3-repository/${a3.a3Id}`)}
                className="a3-card-action-btn a3-card-btn-open"
                title="Open"
              >
                Open
              </button>
              <button
                onClick={() => navigate(`/wms-upload-ui/ie-hub/a3-builder?a3Id=${a3.a3Id}`)}
                className="a3-card-action-btn a3-card-btn-edit"
                title="Edit"
              >
                Edit
              </button>
              <button
                onClick={async () => {
                  try {
                    // Fetch complete A3 data from API
                    const apiUrl = `${Environment.viewA3FormAPI()}?a3Id=${a3.a3Id}`;
                    const response = await axios.get(apiUrl);
                    const fullA3Data = response.data;
                    
                    // Export to PDF
                    exportA3ToPdf(fullA3Data);
                    showSuccess('A3 exported to PDF successfully!');
                  } catch (err) {
                    console.error('Export failed:', err);
                    showError('Failed to export A3 to PDF. Please try again.');
                  }
                }}
                className="a3-card-action-btn a3-card-btn-export"
                title="Export"
              >
                Export
              </button>
              <button
                onClick={async () => {
                  if (pendingDeleteId !== a3.a3Id) {
                    setPendingDeleteId(a3.a3Id);
                    return;
                  }
                  setPendingDeleteId(null);
                  try {
                    const apiUrl = `${Environment.deleteA3FormAPI()}?a3Id=${a3.a3Id}&userId=${auth.userId}`;
                    await axios.delete(apiUrl);
                    setA3Forms(prev => prev.filter(f => f.a3Id !== a3.a3Id));
                    showSuccess(SUCCESS_MESSAGES.DELETED);
                  } catch (err) {
                    console.error(err);
                    showError(ERROR_MESSAGES.DELETE_FAILED);
                  }
                }}
                onBlur={() => setPendingDeleteId(null)}
                className={`a3-card-action-btn ${pendingDeleteId === a3.a3Id ? 'a3-card-btn-confirm-delete' : 'a3-card-btn-delete'}`}
                title="Delete"
              >
                {pendingDeleteId === a3.a3Id ? 'Confirm?' : 'Delete'}
              </button>
            </div>
          </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default A3Repository;